import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { Alert, Chip, Group, SimpleGrid, Stack, Text, TextInput } from '@mantine/core';
import type { AxiosError } from 'axios';
import { useTheme } from '@contexts/ThemeContext';
import { useLanguage } from '@contexts/LanguageContext';
import { DEMO_INTERESTS, DEMO_VOLUMES, type DemoProfile } from '@utils/demo-account';
import { useDemoMe, useSaveDemoProfile } from '../api';
import { resolveFirstLoginCopy } from '../i18n';

export interface StepProfilHandle {
  /** Validates and saves. Resolves false when nothing was saved. */
  save: () => Promise<boolean>;
}

const EMPTY: DemoProfile = { name: '', company: '', email: '', phone: '', position: '', volume: '', interests: [] };
const EMAIL = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

type FieldErrors = Partial<Record<'name' | 'company' | 'email' | 'phone', string>>;

/**
 * A demo account's first step: who the prospect really is, so sales can
 * follow up. Pre-filled from what sales typed when creating the demo.
 * Required: the wizard offers no way past it until it is saved.
 */
const StepProfil = forwardRef<StepProfilHandle>((_props, ref) => {
  const { colors: themeColors } = useTheme();
  const { language } = useLanguage();
  const t = (key: string) => resolveFirstLoginCopy(language, key);
  const me = useDemoMe(true);
  const saveProfile = useSaveDemoProfile();

  const [form, setForm] = useState<DemoProfile>(EMPTY);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [serverError, setServerError] = useState<string | null>(null);
  // Fill from the server once; never overwrite what the prospect is typing.
  const prefilled = useRef(false);
  useEffect(() => {
    const profile = me.data?.profile;
    if (prefilled.current || !profile) return;
    prefilled.current = true;
    setForm({ ...EMPTY, ...profile, interests: profile.interests ?? [] });
  }, [me.data]);

  const set = <K extends keyof DemoProfile>(key: K, value: DemoProfile[K]) => {
    setForm((previous) => ({ ...previous, [key]: value }));
    if (key in errors) setErrors((previous) => ({ ...previous, [key]: undefined }));
  };

  const validate = (): boolean => {
    const next: FieldErrors = {};
    if (!form.name.trim()) next.name = t('profil.error.required');
    if (!form.company.trim()) next.company = t('profil.error.required');
    if (!form.email.trim()) next.email = t('profil.error.required');
    else if (!EMAIL.test(form.email.trim())) next.email = t('profil.error.email');
    const digits = form.phone.replace(/\D/g, '');
    if (!form.phone.trim()) next.phone = t('profil.error.required');
    else if (digits.length < 8 || digits.length > 15) next.phone = t('profil.error.phone');
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const save = async () => {
    setServerError(null);
    if (!validate()) return false;
    try {
      await saveProfile.mutateAsync({
        ...form,
        name: form.name.trim(),
        company: form.company.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        position: form.position.trim(),
      });
      return true;
    } catch (error) {
      const message = (error as AxiosError<{ error?: { message?: string } }>)?.response?.data?.error?.message;
      setServerError(message || t('profil.error.save'));
      return false;
    }
  };

  useImperativeHandle(ref, () => ({ save }));

  const inputStyles = {
    label: { color: themeColors.text, fontWeight: 500 },
    input: { backgroundColor: themeColors.formFields, color: themeColors.text, borderColor: themeColors.border },
  };
  const busy = saveProfile.isPending;

  return (
    <Stack gap="md">
      <div>
        <Text component="h3" fw={700} style={{ color: themeColors.text, fontSize: 22, margin: 0, lineHeight: 1.2 }}>
          {t('profil.heading')}
        </Text>
        <Text size="sm" style={{ color: themeColors.textSecondary, marginTop: 6 }}>
          {t('profil.lead')}
        </Text>
      </div>

      <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
        <TextInput
          label={t('profil.field.name')}
          placeholder={t('profil.placeholder.name')}
          value={form.name}
          onChange={(event) => set('name', event.currentTarget.value)}
          error={errors.name}
          disabled={busy}
          withAsterisk
          styles={inputStyles}
          data-autofocus
        />
        <TextInput
          label={t('profil.field.position')}
          placeholder={t('profil.placeholder.position')}
          value={form.position}
          onChange={(event) => set('position', event.currentTarget.value)}
          disabled={busy}
          styles={inputStyles}
        />
      </SimpleGrid>
      <TextInput
        label={t('profil.field.company')}
        placeholder={t('profil.placeholder.company')}
        value={form.company}
        onChange={(event) => set('company', event.currentTarget.value)}
        error={errors.company}
        disabled={busy}
        withAsterisk
        styles={inputStyles}
      />
      <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
        <TextInput
          type="email"
          label={t('profil.field.email')}
          placeholder={t('profil.placeholder.email')}
          value={form.email}
          onChange={(event) => set('email', event.currentTarget.value)}
          error={errors.email}
          disabled={busy}
          withAsterisk
          styles={inputStyles}
        />
        <TextInput
          type="tel"
          label={t('profil.field.phone')}
          placeholder={t('profil.placeholder.phone')}
          value={form.phone}
          onChange={(event) => set('phone', event.currentTarget.value)}
          error={errors.phone}
          disabled={busy}
          withAsterisk
          styles={inputStyles}
        />
      </SimpleGrid>

      <div>
        <Text size="sm" fw={500} style={{ color: themeColors.text, marginBottom: 6 }}>
          {t('profil.field.volume')}
        </Text>
        {/* Chips, not a Select: a portalled dropdown inside this full-screen modal is unreliable. */}
        <Chip.Group value={form.volume} onChange={(value) => set('volume', String(value))}>
          <Group gap="xs">
            {DEMO_VOLUMES.map((volume) => (
              <Chip key={volume} value={volume} disabled={busy}>
                {t(`profil.volume.${volume}`)}
              </Chip>
            ))}
          </Group>
        </Chip.Group>
      </div>

      <div>
        <Text size="sm" fw={500} style={{ color: themeColors.text, marginBottom: 6 }}>
          {t('profil.field.interests')}
        </Text>
        <Chip.Group multiple value={form.interests} onChange={(value) => set('interests', value)}>
          <Group gap="xs">
            {DEMO_INTERESTS.map((interest) => (
              <Chip key={interest} value={interest} disabled={busy}>
                {t(`profil.interest.${interest}`)}
              </Chip>
            ))}
          </Group>
        </Chip.Group>
      </div>

      {serverError && (
        <Alert color="red" variant="light">
          {serverError}
        </Alert>
      )}
    </Stack>
  );
});

StepProfil.displayName = 'StepProfil';

export default StepProfil;
